import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meetingsApi } from '@/lib/api';

// ─── Meeting CRUD hooks ───

export function useMeetings(params?: { contactId?: string; dealId?: string }) {
  return useQuery({
    queryKey: ['meetings', params],
    queryFn: () => meetingsApi.list(params),
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: ['meetings', id],
    queryFn: () => meetingsApi.getById(id!),
    enabled: !!id,
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof meetingsApi.update>[1] }) =>
      meetingsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useToggleActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, index }: { id: string; index: number }) =>
      meetingsApi.toggleActionItem(id, index),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

// ─── AI Association hooks ───

export function useAssociateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dealId }: { id: string; dealId: string }) =>
      meetingsApi.associate(id, dealId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.acceptSuggestion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useRejectSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dealId }: { id: string; dealId?: string }) =>
      meetingsApi.rejectSuggestion(id, dealId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useReassociateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.reassociate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

// ─── Fathom integration hooks ───

export function useFathomStatus() {
  return useQuery({
    queryKey: ['fathom-status'],
    queryFn: () => meetingsApi.getFathomStatus(),
  });
}

export function useFathomConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) => meetingsApi.connectFathom(apiKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fathom-status'] });
    },
  });
}

export function useFathomDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => meetingsApi.disconnectFathom(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fathom-status'] });
    },
  });
}

export function useFathomSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => meetingsApi.syncFathom(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['fathom-status'] });
    },
  });
}

export function useFathomCalls(params?: { limit?: number; after?: string }) {
  return useQuery({
    queryKey: ['fathom-calls', params],
    queryFn: () => meetingsApi.listFathomCalls(params),
  });
}
